import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { Icon, Grid } from "semantic-ui-react";
import { routes } from "../../../../../store/routes";
import { trackingReportsService } from "../../../../../store/services/ServiceTrackingReports";
import RegionMap from "../../analytics/RegionMap";

const C = {
    pink: "#d60c8c",
    blue: "#2185d0",
    green: "#21ba45",
    orange: "#f2711c",
    purple: "#6435c9",
    grey: "#9aa0a6",
};

const MAP_METRICS = [
    { key: "search_count", label: "Dopyt (vyhľadávania)", short: "Dopyt", color: C.pink },
    { key: "supply_count", label: "Ponuka (aktivity)", short: "Ponuka", color: C.blue },
    { key: "no_results_count", label: "Vyhľadávanie bez výsledkov", short: "Vyhľadávanie bez výsledkov", color: C.orange },
];

const shell = {
    borderRadius: "10px",
    border: "1px solid #eee",
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
    background: "#fff",
    marginBottom: "1.75rem",
    overflow: "hidden",
};

function buildRangeParams(days) {
    const to = moment().format("YYYY-MM-DD");
    const from = moment().subtract(days - 1, "days").format("YYYY-MM-DD");
    return `?from=${from}&to=${to}`;
}

function buildInsight(overview, demandGeo) {
    const totals = overview?.totals || {};
    const prevTotals = overview?.prev_totals || {};
    const funnel = overview?.funnel || {};
    const searches = (totals.search || 0) + (totals.search_no_results || 0);
    const prevSearches = (prevTotals.search || 0) + (prevTotals.search_no_results || 0);
    const noResultsRate = searches > 0 ? Math.round(((totals.search_no_results || 0) / searches) * 100) : 0;
    const l1 = funnel.l1 || 0;
    const l4 = funnel.l4 || 0;
    const searchToCart = l1 > 0 ? Math.round((l4 / l1) * 100) : 0;

    if (searches === 0) {
        return { text: "Keď používatelia začnú hľadať na mape, tu uvidíte dopyt, ponuku a miesta bez výsledkov.", tone: C.grey };
    }
    if (noResultsRate >= 20) {
        return { text: `${noResultsRate}% vyhľadávaní bez výsledkov — na mape prepnite „Vyhľadávanie bez výsledkov“.`, tone: C.orange };
    }
    const regions = demandGeo?.regions || [];
    const noSupply = regions.filter((r) => (r.search_count || 0) > 0 && !(r.supply_count || 0));
    if (noSupply.length > 0) {
        const names = noSupply.slice(0, 2).map((r) => r.region.replace(" kraj", "")).join(", ");
        return { text: `Dopyt bez ponuky: ${names}${noSupply.length > 2 ? "…" : ""}.`, tone: C.orange };
    }
    if (l1 >= 3 && searchToCart >= 15) {
        return { text: `${searchToCart}% vyhľadávaní viedlo do košíka.`, tone: C.green };
    }
    if (prevSearches > 0 && searches > prevSearches) {
        const lift = Math.round(((searches - prevSearches) / prevSearches) * 100);
        return { text: `Vyhľadávania +${lift}% oproti minulému obdobiu.`, tone: C.green };
    }
    return { text: `${searches} vyhľadávaní · ${totals.detail_view || 0} aktivít · ${totals.cart_add || 0} do košíka.`, tone: C.grey };
}

function delta(cur, prev) {
    return prev > 0 ? Math.round(((cur - prev) / prev) * 100) : null;
}

function KpiCard({ icon, color, value, label, kpiDelta }) {
    return (
        <div style={{
            flex: "1 1 200px",
            background: "var(--white)",
            borderRadius: "8px",
            border: "1px solid #eee",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            padding: "1.2rem 1.4rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            borderLeft: `4px solid ${color}`,
        }}>
            <div style={{ textAlign: "left", minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
                    <span style={{ fontSize: "1.9rem", fontWeight: "bold", lineHeight: 1 }}>{value}</span>
                    {kpiDelta != null && (
                        <span style={{ fontSize: "0.85rem", fontWeight: "bold", color: kpiDelta >= 0 ? C.green : "#db2828" }}>
                            <Icon name={kpiDelta >= 0 ? "caret up" : "caret down"} style={{ margin: 0 }} />
                            {Math.abs(kpiDelta)}%
                        </span>
                    )}
                </div>
                <div style={{ color: "#888", fontSize: "0.95rem", marginTop: "0.3rem" }}>
                    {label}
                    {kpiDelta != null && <span style={{ color: "#6b7280", fontSize: "0.85rem" }}> vs. predošlé obdobie</span>}
                </div>
            </div>
            <Icon name={icon} size="big" style={{ color, margin: 0, flexShrink: 0 }} />
        </div>
    );
}

function AnalyticsInsights() {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [insight, setInsight] = useState(null);
    const [regions, setRegions] = useState([]);
    const [mapMetric, setMapMetric] = useState("search_count");
    const [selectedRegion, setSelectedRegion] = useState(null);

    useEffect(() => {
        let active = true;
        async function load() {
            setLoading(true);
            const params = buildRangeParams(7);
            const [overviewRes, geoRes] = await Promise.all([
                trackingReportsService.getOverview(params),
                trackingReportsService.getDemandGeo(params),
            ]);
            if (!active) return;
            const geo = geoRes?.status === 200 ? geoRes.response : {};
            if (overviewRes?.status === 200) {
                const overview = overviewRes.response;
                const totals = overview.totals || {};
                const prevTotals = overview.prev_totals || {};
                const funnel = overview.funnel || {};
                const searches = (totals.search || 0) + (totals.search_no_results || 0);
                const prevSearches = (prevTotals.search || 0) + (prevTotals.search_no_results || 0);
                const l1 = funnel.l1 || 0;
                const l4 = funnel.l4 || 0;
                const searchToCart = l1 > 0 ? Math.round((l4 / l1) * 100) : 0;
                const showConversion = l1 >= 3 && (l4 > 0 || searchToCart >= 10);
                const kpis = [
                    { icon: "search", color: C.pink, value: searches, label: "Vyhľadávaní", kpiDelta: delta(searches, prevSearches) },
                    { icon: "eye", color: C.blue, value: totals.detail_view || 0, label: "Zobrazenie aktivít", kpiDelta: delta(totals.detail_view || 0, prevTotals.detail_view || 0) },
                    { icon: "shopping cart", color: C.green, value: totals.cart_add || 0, label: "Pridaní do košíka", kpiDelta: delta(totals.cart_add || 0, prevTotals.cart_add || 0) },
                ];
                if (showConversion) {
                    kpis.push({ icon: "filter", color: C.purple, value: `${searchToCart}%`, label: "Vyhľadávanie → košík", kpiDelta: null });
                }
                setSummary({ searches, kpis });
                setInsight(buildInsight(overview, geo));
            }
            setRegions(geo.regions || []);
            setLoading(false);
        }
        load();
        return () => { active = false; };
    }, []);

    const metricMeta = MAP_METRICS.find((m) => m.key === mapMetric) || MAP_METRICS[0];

    const mapValues = useMemo(() => (
        regions.reduce((acc, r) => {
            acc[r.region] = r[mapMetric] == null ? 0 : r[mapMetric];
            return acc;
        }, {})
    ), [regions, mapMetric]);

    const selectedDetail = selectedRegion ? regions.find((r) => r.region === selectedRegion) : null;

    const detailRows = selectedDetail
        ? [
            { label: "Vyhľadávania", value: selectedDetail.search_count, color: C.pink },
            { label: "Vyhľadávanie bez výsledkov", value: selectedDetail.no_results_count, color: C.orange },
            { label: "Ponuka", value: selectedDetail.supply_count, color: C.blue },
            { label: "Do košíka", value: selectedDetail.cart_adds, color: C.green },
        ]
        : [];

    const ratioInfo = (() => {
        if (!selectedDetail) return null;
        const ratio = selectedDetail.demand_supply_ratio;
        if (ratio == null) return { ratio: null, text: "Žiadna ponuka", color: C.grey };
        if (ratio >= 5) return { ratio, text: "Vysoký dopyt", color: C.orange };
        if (ratio >= 2) return { ratio, text: "Dopyt > ponuka", color: C.orange };
        return { ratio, text: "Vyvážené", color: C.green };
    })();

    const rankedRegions = useMemo(() => (
        [...regions]
            .filter((r) => (r.search_count || 0) + (r.supply_count || 0) + (r.no_results_count || 0) > 0)
            .sort((a, b) => (Number(b[mapMetric]) || 0) - (Number(a[mapMetric]) || 0))
            .slice(0, 8)
    ), [regions, mapMetric]);

    if (loading) {
        return <div style={{ ...shell, padding: "1.5rem", minHeight: "200px", opacity: 0.5 }} />;
    }

    return (
        <div style={shell}>
            <div style={{
                padding: "1rem 1.25rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.75rem",
                borderBottom: "1px solid #f0f1f3",
            }}>
                <div>
                    <div style={{ fontWeight: "bold", fontSize: "1.05rem", color: "#222" }}>Správanie na mape</div>
                    <div style={{ fontSize: "0.82rem", color: "#6b7280", marginTop: "0.15rem" }}>Posledných 7 dní</div>
                </div>
                <Link
                    to={routes.ANALYTICS}
                    style={{ color: C.pink, fontWeight: "bold", fontSize: "0.88rem", textDecoration: "none" }}
                >
                    Otvoriť analytiku <Icon name="arrow right" style={{ margin: 0 }} />
                </Link>
            </div>

            {summary && summary.searches > 0 && (
                <div style={{
                    padding: "1rem 1.25rem",
                    display: "flex",
                    gap: "1rem",
                    flexWrap: "wrap",
                    borderBottom: "1px solid #f0f1f3",
                }}>
                    {summary.kpis.map((kpi) => (
                        <KpiCard key={kpi.label} {...kpi} />
                    ))}
                </div>
            )}

            <div style={{ padding: "1rem 1.25rem 1.25rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.85rem" }}>
                    <div style={{ fontSize: "1.05rem", fontWeight: "bold", color: "#222" }}>Mapa podľa kraja</div>
                    <div style={{ display: "inline-flex", background: "#f1f1f3", borderRadius: "20px", padding: "3px" }}>
                        {MAP_METRICS.map((m) => {
                            const active = mapMetric === m.key;
                            return (
                                <span
                                    key={m.key}
                                    onClick={() => { setMapMetric(m.key); setSelectedRegion(null); }}
                                    style={{
                                        cursor: "pointer",
                                        padding: "5px 14px",
                                        borderRadius: "16px",
                                        fontSize: "0.84rem",
                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                        color: active ? "#fff" : "#888",
                                        background: active ? m.color : "transparent",
                                    }}
                                >
                                    {m.short}
                                </span>
                            );
                        })}
                    </div>
                </div>

                <Grid stackable>
                    <Grid.Column width={10}>
                        <RegionMap
                            values={mapValues}
                            selected={selectedRegion}
                            onSelect={setSelectedRegion}
                            valueLabel={metricMeta.short}
                            toColor={metricMeta.color}
                            formatValue={(v) => (v == null ? "—" : v)}
                        />
                    </Grid.Column>
                    <Grid.Column width={6}>
                        {selectedDetail ? (
                            <div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                                    <div style={{ fontWeight: "bold", fontSize: "1rem", color: "#222" }}>
                                        {selectedRegion.replace(" kraj", "")}
                                    </div>
                                    <span
                                        onClick={() => setSelectedRegion(null)}
                                        style={{ cursor: "pointer", color: C.grey, fontSize: "0.82rem" }}
                                    >
                                        <Icon name="close" style={{ margin: 0 }} />
                                    </span>
                                </div>
                                {detailRows.map((row) => (
                                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem 0", borderBottom: "1px solid #f1f1f3", fontSize: "0.9rem" }}>
                                        <span style={{ color: "#555" }}>{row.label}</span>
                                        <strong style={{ color: row.color }}>{row.value ?? 0}</strong>
                                    </div>
                                ))}
                                {ratioInfo && (
                                    <div style={{ marginTop: "0.75rem", padding: "0.65rem 0.85rem", borderRadius: "8px", background: "#f7f8fa", fontSize: "0.85rem" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                            <span style={{ color: "#555" }}>Dopyt / ponuka</span>
                                            <strong style={{ color: ratioInfo.color }}>
                                                {ratioInfo.ratio == null ? "—" : ratioInfo.ratio}
                                            </strong>
                                        </div>
                                        <div style={{ color: ratioInfo.color, marginTop: "0.25rem", fontSize: "0.78rem" }}>{ratioInfo.text}</div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                {insight && (
                                    <div style={{ fontSize: "0.85rem", color: insight.tone, marginBottom: "0.85rem", lineHeight: 1.45 }}>
                                        {insight.text}
                                    </div>
                                )}
                                {rankedRegions.length > 0 ? (
                                    <>
                                        <div style={{ fontSize: "0.72rem", color: "#9aa0a6", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: "0.5rem" }}>
                                            Poradie — {metricMeta.short}
                                        </div>
                                        {rankedRegions.map((r, idx) => (
                                            <div
                                                key={r.region}
                                                onClick={() => setSelectedRegion(r.region)}
                                                style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.45rem 0", cursor: "pointer", borderBottom: "1px solid #f6f6f8" }}
                                            >
                                                <span style={{
                                                    width: 20, height: 20, borderRadius: "50%", background: "#f1f1f3",
                                                    color: "#888", fontSize: "0.75rem", fontWeight: "bold",
                                                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                                }}>
                                                    {idx + 1}
                                                </span>
                                                <span style={{ flex: 1, color: "#333", fontSize: "0.88rem" }}>{r.region.replace(" kraj", "")}</span>
                                                <strong style={{ color: metricMeta.color, fontSize: "0.95rem" }}>
                                                    {r[mapMetric] == null ? "—" : r[mapMetric]}
                                                </strong>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <div style={{ color: "#9aa0a6", fontSize: "0.88rem" }}>
                                        <Icon name="hand pointer" /> Klikni na kraj v mape pre detail.
                                    </div>
                                )}
                            </div>
                        )}
                    </Grid.Column>
                </Grid>
            </div>
        </div>
    );
}

export default AnalyticsInsights;
